import type { Identity } from '../types/auth';

/**
 * PR Management (partnerships and the partner hub) permission groups,
 * mirroring argus-backend `pr_read` / `pr_edit` / `pr_admin`.
 *
 * Anyone on the PR panel may read and edit, including setting either partner
 * tier; deleting records, managing tags and forcing held Discord removals need
 * PR admin. `SPY_Super_admin` keeps the usual Argus override.
 *
 * Client-side checks are for UX only; the backend verifies every request.
 */
export const PARTNERSHIP_GROUPS = {
  read: ['argus_pr_panel', 'argus_pr_admin', 'SPY_Super_admin'],
  edit: ['argus_pr_panel', 'argus_pr_admin', 'SPY_Super_admin'],
  admin: ['argus_pr_admin', 'SPY_Super_admin'],
} as const;

export interface PartnershipPerms {
  /** See partnerships, creators, modpacks and partner pages. */
  canView: boolean;
  /** Create and change partnerships and partner teams, and set either tier. */
  canEdit: boolean;
  /** Delete records, manage tags, and apply Discord removals held back by the safety cap. */
  canAdmin: boolean;
}

function groupsOf(identity: Identity | null | undefined): string[] {
  const groups = identity?.raw?.custom?.groups;
  return Array.isArray(groups) ? groups : [];
}

export function getPartnershipPerms(identity: Identity | null | undefined, isDev: boolean): PartnershipPerms {
  if (isDev) return { canView: true, canEdit: true, canAdmin: true };
  const held = groupsOf(identity);
  const hasAny = (wanted: readonly string[]) => wanted.some((group) => held.includes(group));
  return {
    canView: hasAny(PARTNERSHIP_GROUPS.read),
    canEdit: hasAny(PARTNERSHIP_GROUPS.edit),
    canAdmin: hasAny(PARTNERSHIP_GROUPS.admin),
  };
}

/** Shared by the route guard in App.tsx, the sidebar entry and every cross-module link into PR Management. */
export const canViewPartnerships = (identity: Identity | null | undefined): boolean =>
  getPartnershipPerms(identity, false).canView;
